import React from 'react';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/browser';
import {
  HeadingText30,
  Input,
  RadioGroup,
  InputType,
  Checkbox,
  BodyTextSmall,
  SelectField,
} from '@modernhealth/vital-components';

import { filterNullish } from 'src/shared/typeUtils/filterNullish';
import { captureException } from 'src/shared/utils/captureException';

import { CircleRsvpQuestionFragment as QuestionFragment } from '../documents/topic.generated';
import { QuestionAnswer } from '../types';

const QuestionWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  marginBottom: '24px',
});

interface QuestionProps {
  answer: string | boolean;
  setAnswer: (answer: QuestionAnswer) => void;
  question: QuestionFragment;
  showErrors: boolean;
}

const QuestionTextWrapper = styled.p({
  marginBottom: '16px',
});

const Question: React.FC<QuestionProps> = ({ question, answer, setAnswer, showErrors }) => {
  const options = React.useMemo(() => {
    if (question?.__typename === 'CircleDropdownQuestionNode') {
      return filterNullish(question.options ?? []).map(option => {
        return { label: option.optionText, value: option.id };
      });
    }
    return [];
  }, [JSON.stringify(question && 'options' in question ? question.options : [])]);

  if (!question) return null;

  const renderQuestionInput = () => {
    const errMsg = 'An unknown component was attempted to be rendered on Circles RSVP form';
    switch (question.__typename) {
      case 'CircleDropdownQuestionNode':
        return (
          <>
            <label>
              <HeadingText30 as="span">{question?.questionText}</HeadingText30>
              <div css={{ marginTop: '16px' }}>
                <SelectField
                  options={options}
                  onChange={option => {
                    setAnswer({ id: question.id, answerId: option?.value });
                  }}
                  value={typeof answer === 'string' ? answer : null}
                  fieldId={`question-${question.id}`}
                  error={showQuestionError ? 'This question is required' : undefined}
                />
              </div>
            </label>
          </>
        );
      case 'CircleRadioGroupQuestionNode':
        return (
          <>
            <RadioGroup
              legend={question?.questionText}
              onChange={(value: string) => {
                setAnswer({ id: question.id, answerId: value });
              }}
              selected={`${answer}`}
              groupName={`question-${question.id}`}
              options={
                filterNullish(question.options ?? []).map(option => {
                  return { label: option.optionText, value: option.id };
                }) || []
              }
            />
          </>
        );
      case 'CircleSingleLineQuestionNode':
        return (
          <>
            <QuestionTextWrapper style={{ marginBottom: '16px' }} aria-hidden>
              <HeadingText30 as="span">{question?.questionText}</HeadingText30>
            </QuestionTextWrapper>
            <Input
              type={InputType.TextField}
              label={question.questionText}
              useDefaultPlaceholder
              fieldId={`question-${question.id}`}
              required={question.required}
              onChange={(value: string | React.ChangeEvent<HTMLInputElement>) => {
                setAnswer({ id: question.id, answerText: value.toString() });
              }}
              value={answer.toString()}
              error={showQuestionError ? 'This question is required' : undefined}
            />
          </>
        );
      case 'CircleParagraphQuestionNode':
        return (
          <>
            <QuestionTextWrapper style={{ marginBottom: '16px' }} aria-hidden>
              <HeadingText30 as="span">{question?.questionText}</HeadingText30>
            </QuestionTextWrapper>
            <Input
              type={InputType.TextArea}
              label={question.questionText}
              useDefaultPlaceholder
              fieldId={`question-${question.id}`}
              required={question.required}
              value={answer.toString()}
              onChange={(value: string | React.ChangeEvent<HTMLInputElement>) => {
                setAnswer({ id: question.id, answerText: value.toString() });
              }}
              error={showQuestionError ? 'This question is required' : undefined}
            />
          </>
        );
      case 'CircleCheckboxQuestionNode':
        return (
          <Checkbox
            value={!!answer}
            label={question.questionText}
            onChange={value => {
              setAnswer({ id: question.id, answerText: value });
            }}
          />
        );
      default:
        Sentry.captureException(new Error(errMsg));
        captureException(new Error(errMsg));
        return null;
    }
  };

  const needsHelperText =
    question?.__typename === 'CircleCheckboxQuestionNode' ||
    question?.__typename === 'CircleRadioGroupQuestionNode';
  const showQuestionError = showErrors && !answer && question.required;
  const errorText =
    question?.__typename === 'CircleCheckboxQuestionNode'
      ? 'This Circle requires you to confirm this statement'
      : 'This question is required';
  return (
    <QuestionWrapper>
      {renderQuestionInput()}
      {showQuestionError && needsHelperText && (
        <BodyTextSmall css={{ paddingLeft: '8px', paddingTop: '8px' }} fontColor="red55">
          {errorText}
        </BodyTextSmall>
      )}
    </QuestionWrapper>
  );
};

export default Question;
